import type { ClipboardEvent } from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import FormHelperText from '@mui/material/FormHelperText';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import Checkbox from '@mui/material/Checkbox';
import type { FieldDef, FieldOption, FormValues } from '../types';
import DateTimePickerField from './DateTimePickerField';
import CurrencyField from './CurrencyField';
import TagsField from './TagsField';
import BulkImportActions, { buildBulkImportValue, parseBulkImportText, type BulkImportMode } from './BulkImportActions';

interface FormFieldProps {
  field: FieldDef;
  values: FormValues;
  onChange: (key: string, value: string | string[]) => void;
  externalError?: string;
}

function tokenizeText(raw: string): string[] {
  return raw
    .split(/[\n,;]+/)
    .map((v) => v.trim())
    .filter(Boolean);
}

function getCrossFieldError(field: FieldDef, values: FormValues): string | undefined {
  if (!field.excludeValuesFrom) return undefined;
  const otherRaw = values[field.excludeValuesFrom];
  if (!otherRaw || typeof otherRaw !== 'string' || !otherRaw.trim()) return undefined;
  const thisRaw = values[field.key];
  if (!thisRaw || typeof thisRaw !== 'string' || !thisRaw.trim()) return undefined;
  const otherTokens = new Set(tokenizeText(otherRaw));
  const duplicates = tokenizeText(thisRaw as string).filter((v) => otherTokens.has(v));
  if (duplicates.length === 0) return undefined;
  return `Valor(es) já presentes no campo oposto: ${duplicates.join(', ')}`;
}

function getClipboardText(event: ClipboardEvent<HTMLElement>) {
  return event.clipboardData.getData('text').trim();
}

export default function FormField({ field, values, onChange, externalError }: FormFieldProps) {
  if (field.conditional) {
    const condValue = values[field.conditional.field];
    const isVisible = Array.isArray(condValue)
      ? condValue.some((v) => field.conditional!.values.includes(v))
      : field.conditional.values.includes(condValue as string);
    if (!isVisible) return null;
  }

  const value = values[field.key] ?? '';

  const handleBulkPaste = (
    event: ClipboardEvent<HTMLElement>,
    mode: BulkImportMode,
    currentValue: string | string[],
    options?: FieldOption[],
    excludedValues?: string[]
  ) => {
    if (!field.bulkImport) return;

    const text = getClipboardText(event);
    if (!text) return;

    const result = buildBulkImportValue({
      mode,
      options,
      currentValue,
      excludedValues,
      rawValues: parseBulkImportText(text),
    });

    if (!result.applied || result.value === undefined) return;

    event.preventDefault();
    onChange(field.key, result.value);
  };

  if (field.type === 'info') {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1.5,
          bgcolor: 'action.hover',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 18, color: 'primary.main', flexShrink: 0 }} />
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            {field.label}
          </Typography>
          <Typography variant="body2" color="primary.main" fontWeight={600}>
            {field.helperText}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (field.type === 'select') {
    const opts = field.options ?? [];
    //const selectedOpt = opts.find((o) => o.value === (value as string)) ?? null;
    const selectedOpt = opts.find((o) => String(o.value) === String(value)) ?? null;
    return (
      <Box>
        <Autocomplete
          size="small"
          fullWidth
          options={opts}
          value={selectedOpt}
          onChange={(_e, newVal) => onChange(field.key, (newVal as FieldOption | null)?.value ?? '')}
          getOptionLabel={(opt) => opt.label}
          isOptionEqualToValue={(opt, val) => opt.value === val.value}
          renderInput={(params) => (
            <TextField
              {...params}
              label={`${field.label}${field.required ? ' *' : ''}`}
              placeholder="Buscar..."
              onPaste={(event) => handleBulkPaste(event, 'singleOption', value as string, opts)}
            />
          )}
          noOptionsText="Sem opções"
        />
        {field.bulkImport && (
          <BulkImportActions
            mode="singleOption"
            options={opts}
            currentValue={value as string}
            onApply={(nextValue) => onChange(field.key, nextValue as string)}
          />
        )}
        {field.helperText && <FormHelperText>{field.helperText}</FormHelperText>}
      </Box>
    );
  }

  if (field.type === 'multiselect') {
    //const selected = Array.isArray(value) ? value : value ? [value as string] : [];
    const selected = Array.isArray(value)
  ? value.map((v) => String(v))
  : value
    ? [String(value)]
    : [];
    const excludedValues = field.excludeValuesFrom
      ? (Array.isArray(values[field.excludeValuesFrom])
          ? (values[field.excludeValuesFrom] as string[])
          : values[field.excludeValuesFrom]
            ? [values[field.excludeValuesFrom] as string]
            : [])
      : [];
    const availableOptions = field.options?.filter((opt) => !excludedValues.includes(opt.value)) ?? [];
    const selectedOpts = availableOptions.filter((o) => selected.includes(o.value));
    return (
      <Box>
        <Autocomplete
          multiple
          size="small"
          fullWidth
          options={availableOptions}
          value={selectedOpts}
          onChange={(_e, newVal) => onChange(field.key, (newVal as FieldOption[]).map((o) => o.value))}
          getOptionLabel={(opt) => opt.label}
          isOptionEqualToValue={(opt, val) => opt.value === val.value}
          disableCloseOnSelect
          filterSelectedOptions={false}
          renderOption={(props, opt, { selected: isSelected }) => {
            const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key: React.Key };
            return (
              <li key={key} {...rest}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  style={{ marginRight: 8 }}
                  checked={isSelected}
                />
                {opt.label}
              </li>
            );
          }}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((opt, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return <Chip key={key} label={opt.label} size="small" {...tagProps} />;
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={`${field.label}${field.required ? ' *' : ''}`}
              placeholder={selectedOpts.length === 0 ? 'Buscar...' : ''}
              onPaste={(event) => handleBulkPaste(event, 'options', selected, availableOptions, excludedValues)}
            />
          )}
          noOptionsText="Sem opções"
        />
        {field.bulkImport && (
          <BulkImportActions
            mode="options"
            options={availableOptions}
            currentValue={selected}
            excludedValues={excludedValues}
            onApply={(nextValue) => onChange(field.key, nextValue as string[])}
          />
        )}
        {field.helperText && <FormHelperText>{field.helperText}</FormHelperText>}
      </Box>
    );
  }

  if (field.type === 'tags') {
    return <TagsField field={field} values={values} onChange={onChange} />;
  }

  if (field.type === 'textarea') {
    const crossError = getCrossFieldError(field, values);
    return (
      <Box>
        <TextField
          fullWidth
          size="small"
          multiline
          rows={field.rows ?? 3}
          label={field.label}
          value={value as string}
          onChange={(e) => onChange(field.key, e.target.value)}
          onPaste={(event) => handleBulkPaste(event, 'text', value as string)}
          placeholder={field.placeholder}
          helperText={crossError ?? field.helperText}
          error={!!crossError}
          required={field.required}
        />
        {field.bulkImport && (
          <BulkImportActions
            mode="text"
            currentValue={value as string}
            onApply={(nextValue) => onChange(field.key, nextValue as string)}
          />
        )}
      </Box>
    );
  }

  if (field.type === 'number') {
    return (
      <TextField
        fullWidth
        size="small"
        type="number"
        label={field.label}
        value={value as string}
        onChange={(e) => onChange(field.key, e.target.value)}
        placeholder={field.placeholder}
        helperText={field.helperText}
        required={field.required}
      />
    );
  }

  if (field.type === 'currency') {
    return (
      <CurrencyField
        label={field.label}
        value={value as string}
        onChange={(v) => onChange(field.key, v)}
        required={field.required}
        helperText={field.helperText}
        placeholder={field.placeholder}
      />
    );
  }

  if (field.type === 'datetime') {
    return (
      <DateTimePickerField
        label={field.label}
        value={value as string}
        onChange={(v) => onChange(field.key, v)}
        required={field.required}
        error={!!externalError}
        helperText={externalError ?? field.helperText}
      />
    );
  }

  const crossError = getCrossFieldError(field, values);
  return (
    <TextField
      fullWidth
      size="small"
      label={field.label}
      value={value as string}
      onChange={(e) => onChange(field.key, e.target.value)}
      placeholder={field.placeholder}
      helperText={crossError ?? field.helperText}
      error={!!crossError}
      required={field.required}
    />
  );
}
